import React, { useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import * as d3 from 'd3';

interface StatCardProps {
  label: string;
  value: string | number;
  trend?: number[];
  trendDirection?: 'up' | 'down' | 'flat';
  icon?: React.ReactNode;
  color?: string;
  onClick?: () => void;
  accentColor?: 'indigo' | 'purple' | 'blue' | 'cyan' | 'amber' | 'emerald';
}

const ACCENT_BORDER: Record<string, string> = {
  indigo: 'border-l-2 border-indigo-600/30',
  purple: 'border-l-2 border-purple-600/30',
  blue: 'border-l-2 border-blue-600/30',
  cyan: 'border-l-2 border-cyan-600/30',
  amber: 'border-l-2 border-amber-600/30',
  emerald: 'border-l-2 border-emerald-600/30',
};
const ACCENT_GLOW: Record<string, string> = {
  indigo: 'hover:shadow-[0_0_15px_rgba(139,92,246,0.15)]',
  purple: 'hover:shadow-[0_0_15px_rgba(147,51,234,0.15)]',
  blue: 'hover:shadow-[0_0_15px_rgba(59,130,246,0.15)]',
  cyan: 'hover:shadow-[0_0_15px_rgba(6,182,212,0.15)]',
  amber: 'hover:shadow-[0_0_15px_rgba(245,158,11,0.15)]',
  emerald: 'hover:shadow-[0_0_15px_rgba(16,185,129,0.15)]',
};

const StatCard: React.FC<StatCardProps> = ({ label, value, trend, trendDirection, icon, color, onClick, accentColor }) => {
  const { darkMode } = useTheme();
  const sparkRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!trend || !sparkRef.current || trend.length < 2) return;
    const svg = d3.select(sparkRef.current);
    svg.selectAll('*').remove();

    const w = 64, h = 20;
    const x = d3.scaleLinear().domain([0, trend.length - 1]).range([0, w]);
    const y = d3.scaleLinear().domain([d3.min(trend) || 0, d3.max(trend) || 1]).range([h - 2, 2]);

    const line = d3.line<number>()
      .x((_, i) => x(i))
      .y(d => y(d))
      .curve(d3.curveMonotoneX);

    const trendColor = trendDirection === 'up' ? '#22c55e' : trendDirection === 'down' ? '#ef4444' : '#a3a3a3';

    svg.append('path')
      .datum(trend)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', trendColor)
      .attr('stroke-width', 1.5)
      .attr('stroke-linecap', 'round');
  }, [trend, trendDirection]);

  const arrowMap = { up: '\u2197', down: '\u2198', flat: '\u2192' };
  const arrowColor = trendDirection === 'up' ? 'text-green-400' : trendDirection === 'down' ? 'text-red-400' : 'text-gray-400';

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`p-4 rounded-xl border text-left transition-all duration-300 ${onClick ? 'hover:scale-[1.02] hover:translate-y-[-2px] cursor-pointer' : ''} ${
        accentColor ? `${ACCENT_BORDER[accentColor]} ${ACCENT_GLOW[accentColor]}` : ''
      } ${
        darkMode
          ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700/60 hover:border-purple-600/40 hover:shadow-lg hover:shadow-purple-900/10'
          : 'bg-white border-gray-200 hover:border-purple-300 hover:shadow-lg hover:shadow-purple-500/5'
      }`}
    >
      <div className="flex items-center justify-between">
        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
        {icon && <span className="opacity-50">{icon}</span>}
      </div>
      <div className="flex items-end justify-between mt-1">
        <p className={`text-2xl font-bold ${color ? '' : darkMode ? 'text-gray-100' : 'text-gray-900'}`} style={color ? { color } : undefined}>
          {value}
        </p>
        <div className="flex items-center gap-1">
          {trend && <svg ref={sparkRef} width={64} height={20} />}
          {trendDirection && <span className={`text-sm ${arrowColor}`}>{arrowMap[trendDirection]}</span>}
        </div>
      </div>
    </button>
  );
};

export default StatCard;
